import React, {useCallback} from 'react';
import {Platform, StyleSheet, View} from 'react-native';
import Styles from '../../Styles';
import Localized from 'src/constants/AppStrings';
import AVText from '../AVText';
import {ensureHttps} from 'src/services/StringUtils';
import {ImageWithPlaceholder} from 'src/components/elements/rewards/RedeemPoints4Rewards';
import {RewardsIcon} from 'src/components/img/svg/rewards';
import RoundedAppButton, {
  RoundedAppButtonType,
} from 'src/components/elements/RoundedAppButton';
import AVTouchableOpacity from 'src/components/elements/AVTouchableOpacity';
import NavActions from 'src/actions/NavActions';
import AppRoutes from 'src/AppRoutes';
import {getButtonDescriber} from 'src/components/elements/buttonDescriptor/DescriptorType';
import {getDescriber as getSnackDescriber} from 'src/components/screens/sendSnack/descriptor/sendasnack/DescriptorType';
import {ButtonType} from 'src/components/elements/RoundedButton';
import {IsConnectedProps} from 'src/types/Screen';
import {getDescriber as getScreenDescriber} from 'src/components/screens/descriptor/DescriptorType';
import withIsConnected from 'src/components/hoc/withIsConnected';
import Settings from 'src/Settings';
import CancelIcon from 'src/components/img/svg/CancelIcon';
import RewardsAddToCart from 'src/components/img/svg/RewardsAddToCart';
import TransactionStore from 'src/stores/TransactionStore';
import {useNearbyLocations} from '../../../Util';

type RedeemTabProps = IsConnectedProps & {
  productName: string;
  point: number;
  imageUrl: string;
  rewardProductId: string;
  productId: string;
  upc?: string[];
  cancel: () => void;
  hideOnly?: () => void;
};
const lineHeightMultiplier = 1.14;
const RedeemTab = (props: RedeemTabProps) => {
  const {
    point,
    imageUrl = '',
    productName,
    upc,
    rewardProductId,
    productId,
  } = props;

  const locationData = useNearbyLocations();
  const isShowButtonAddToMobileOrder = locationData.some(
    (location) => location.hasOnlineOrdering,
  );

  const navigateToRedeemProduct = useCallback(
    async (route: AppRoutes) => {
      if (props.isConnected) {
        props.hideOnly?.();
        NavActions.navigate(AppRoutes.Shop, {
          screen: route,
          params: {
            productName,
            point,
            upc,
            imageUrl,
            rewardProductId,
            productId,
          },
        });
        if (upc && upc.length > 0) {
          TransactionStore.setRewardItem({BarCode: upc[0], Id: productId});
        }
      } else {
        NavActions.navigate(AppRoutes.ServerErrorDialog, {
          errorTitle: Localized.Labels.redeem_rewd,
          errorDesc: Localized.Errors.error_rewards_modal,
          errorBtnName: getScreenDescriber().getGoToSettings(),
        });
        return;
      }
    },
    [props, productName, point, upc, imageUrl, rewardProductId, productId],
  );

  return (
    productName && (
      <View style={styles.container}>
        <AVTouchableOpacity
          aria-label={`X icon, Double tap to ${Localized.Buttons.cancel} offer details`}
          role="button"
          style={styles.closeModalContainer}
          onPress={props.cancel}
          accessible
          accessibilityLabel={Localized.Buttons.cancel}
          accessibilityRole="button"
        >
          <View style={styles.closeModalTitleContainer}>
            <CancelIcon />
          </View>
        </AVTouchableOpacity>
        <View style={styles.content}>
          <View style={styles.titleHead}>
            <AVText
              style={[
                styles.redeemRwd,
                getSnackDescriber()?.snackDetailsBtnFontSTyle(),
              ]}
              accessibilityLabel={Localized.Labels.redeem_rewd}
            >
              {Localized.Labels.redeem_rewd}
            </AVText>
            {Platform.OS === 'web' && (
              <AVText
                style={[
                  styles.redeemRwdmessage,
                  getSnackDescriber()?.snackDetailsBtnFontSTyle(),
                ]}
                accessibilityLabel={Localized.Labels.rewards_redeem_message_web}
              >
                {Localized.Labels.formatString(
                  isShowButtonAddToMobileOrder
                    ? Localized.Labels.rewards_redeem_message_web
                    : Localized.Labels
                        .rewards_redeem_message_web_when_no_dining_location_available,
                  Settings.appDisplayName,
                )}
              </AVText>
            )}
          </View>

          <View style={styles.logoView}>
            <ImageWithPlaceholder
              source={{uri: ensureHttps(imageUrl || 'default')}}
              width={67}
              height={67}
            >
              <RewardsIcon />
            </ImageWithPlaceholder>
          </View>

          <View style={styles.labelStyles}>
            <AVText
              style={[
                styles.pdtName,
                getSnackDescriber()?.snackDetailsBtnFontSTyle(),
              ]}
              accessibilityLabel={productName}
            >
              {productName}
            </AVText>

            <AVText
              style={[
                styles.ptsNumber,
                getSnackDescriber()?.snackDetailsBtnFontSTyle(),
              ]}
              accessibilityLabel={point.toString()}
            >
              {Localized.Labels.formatString(
                Localized.Labels.points_number,
                point,
              )}
            </AVText>
          </View>

          <View style={[styles.productNameHead, styles.buttonsParentsView]}>
            {isShowButtonAddToMobileOrder ? (
              <RoundedAppButton
                accessibilityHintValue="Double tap to open add to mobile order"
                accessibilityLabelValue={Localized.Labels.add_to_mobile_order}
                aria-label={Localized.Labels.add_to_mobile_order}
                buttonType={RoundedAppButtonType.Outline}
                titleText={Localized.Labels.add_to_mobile_order}
                buttonContainerStyle={styles.buttonContainer}
                maxFontSizeMultiplier={Styles.FontSizeMultiplier.maxfm1}
                textStyle={[
                  styles.buttonTextStyle,
                  getSnackDescriber()?.snackDetailsBtnFontSTyle(),
                ]}
                buttonIcon={
                  <RewardsAddToCart
                    color={getButtonDescriber().checkRoundedBtnColor()}
                  />
                }
                buttonViewStyle={styles.buttonViewStyle}
                onPress={() => {
                  navigateToRedeemProduct(AppRoutes.NewMobileOrderLocation);
                }}
              />
            ) : null}
          </View>

          <AVTouchableOpacity
            onPress={props.cancel}
            testID="cancelredeem"
            style={styles.cancelButtonContainer}
          >
            <View
              style={[
                {
                  borderBottomColor: getButtonDescriber().checkRoundedBtnColor(
                    ButtonType.outline,
                  ),
                },
                styles.cancelButtonView,
              ]}
            >
              <AVText
                style={[
                  styles.canceltxt,
                  {
                    color: getButtonDescriber().checkRoundedBtnColor(
                      ButtonType.outline,
                    ),
                  },
                  getSnackDescriber()?.snackDetailsBtnFontSTyle(),
                ]}
                maxFontSizeMultiplier={Styles.FontSizeMultiplier.maxfm8}
              >
                {Localized.Buttons.cancel}
              </AVText>
            </View>
          </AVTouchableOpacity>
        </View>
      </View>
    )
  );
};

const styles = StyleSheet.create({
  container: {
    marginTop: Styles.Spacing.m3,
    gap: Styles.Spacing.m3,
    alignItems: 'center',
    justifyContent: 'center',
  },
  titleHead: {
    gap: Styles.Spacing.m2,
  },
  productNameHead: {
    gap: Styles.Spacing.m3,
  },
  logoView: {
    paddingTop: Styles.Spacing.m4 + Styles.Spacing.m2,
    alignItems: 'center',
    justifyContent: 'center',
  },
  canceltxt: {
    fontSize: Styles.Fonts.f1,
    textAlign: 'center',
    fontFamily: Styles.FontFamily.aeonikRegular,
    fontWeight: '700',
    textDecorationLine: 'none',
    paddingTop: Styles.Spacing.m2,
  },
  redeemRwd: {
    fontSize: Styles.Fonts.f1 + 6,
    fontWeight: '700',
    lineHeight: (Styles.Fonts.f1 + 6) * lineHeightMultiplier,
  },
  redeemRwdmessage: {
    fontSize: Styles.Fonts.sectionHeader,
    fontWeight: '400',
    lineHeight: (Styles.Fonts.f1 + 6) * lineHeightMultiplier,
    fontStyle: 'italic',
    marginTop: Styles.Spacing.m3,
  },
  pdtName: {
    fontSize: Styles.Fonts.f1 + 4,
    fontWeight: '700',
    lineHeight: (Styles.Fonts.f1 + 4) * lineHeightMultiplier,
    textAlign: 'center',
  },
  ptsNumber: {
    fontSize: Styles.Fonts.f1,
    fontWeight: '400',
    lineHeight: Styles.Fonts.f1 * lineHeightMultiplier,
    alignSelf: 'center',
  },
  buttonContainer: {
    overflow: 'hidden',
    alignSelf: 'center',
    justifyContent: 'center',
    alignContent: 'center',
  },
  buttonViewStyle: {
    overflow: 'hidden',
    height: 42,
    borderWidth: 2,
  },
  buttonTextStyle: {
    fontWeight: '700',
    fontSize: Styles.Fonts.f1,
    overflow: 'hidden',
  },
  buttonsParentsView: {
    justifyContent: 'center',
    alignSelf: 'center',
    alignContent: 'center',
  },
  cancelButtonContainer: {
    justifyContent: 'center',
    alignSelf: 'center',
    alignContent: 'center',
    marginVertical: Styles.Spacing.m4,
  },
  cancelButtonView: {
    alignSelf: 'flex-start',
    borderBottomWidth: 1.5,
  },
  closeModalContainer: {
    position: 'absolute',
    top: Styles.Spacing.m1,
    right: Styles.Spacing.m0,
    width: Styles.Heights.touchTargetHeight2,
    height: Styles.Heights.touchTargetHeight2,
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 10,
  },
  closeModalTitleContainer: {
    width: Styles.Spacing.m2 + Styles.Spacing.m3,
    height: Styles.Spacing.m2 + Styles.Spacing.m3,
    justifyContent: 'center',
    alignItems: 'center',
  },
  content: {
    paddingVertical: Styles.Spacing.m3,
    paddingHorizontal: Styles.Spacing.m3,
  },
  labelStyles: {
    marginBottom: Styles.Spacing.m4 + Styles.Spacing.m3,
  },
});

export default withIsConnected(RedeemTab);
